
export interface PaginationButtonsProps {
    page: number;
    totalPages: number;
    onChangePage: (page: number) => void;
}

type PaginationItem = number | 'start-ellipsis' | 'end-ellipsis';

const getPaginationItems = (page: number, totalPages: number): PaginationItem[] => {
    const currentPage = Math.min(Math.max(page, 1), totalPages);
    const maxVisiblePages = 7;

    if (totalPages <= maxVisiblePages) {
        return Array.from({length: totalPages}, (_, i) => i + 1);
    }

    const pages = new Set<number>([1, totalPages]);
    const windowStart = Math.max(2, currentPage - 1);
    const windowEnd = Math.min(totalPages - 1, currentPage + 1);

    for (let pageNumber = windowStart; pageNumber <= windowEnd; pageNumber += 1) {
        pages.add(pageNumber);
    }

    if (currentPage <= 3) {
        pages.add(2);
        pages.add(3);
        pages.add(4);
    }

    if (currentPage >= totalPages - 2) {
        pages.add(totalPages - 3);
        pages.add(totalPages - 2);
        pages.add(totalPages - 1);
    }

    const sortedPages = Array.from(pages)
        .filter((pageNumber) => pageNumber >= 1 && pageNumber <= totalPages)
        .sort((a, b) => a - b);

    return sortedPages.reduce<PaginationItem[]>((items, pageNumber, index) => {
        const previousPage = sortedPages[index - 1];

        if (previousPage && pageNumber - previousPage > 1) {
            items.push(previousPage === 1 ? 'start-ellipsis' : 'end-ellipsis');
        }

        items.push(pageNumber);

        return items;
    }, []);
}

const PaginationButtons: React.FC<PaginationButtonsProps> = (props) => {
    const totalPages = Math.max(1, props.totalPages);
    const currentPage = Math.min(Math.max(props.page, 1), totalPages);
    const paginationItems = getPaginationItems(currentPage, totalPages);

    const onPageClick = (page: number) => {
        if (page !== currentPage) {
            props.onChangePage(page);
        }
    }

    const onBackButtonClick = () => {
        if (currentPage > 1) {
            props.onChangePage(currentPage - 1)
        }
    }

    const onForwardButtonClick = () => {
        if (currentPage < totalPages) {
            props.onChangePage(currentPage + 1)
        }
    }

    return (
        <nav className={'paginated-buttons'} aria-label="Product results pagination">
            <button
                type="button"
                className={'text-button paginated-buttons__arrow'}
                onClick={onBackButtonClick}
                disabled={currentPage <= 1}
                aria-label="Previous page"
            >
                <i className="fas fa-arrow-left"></i>
            </button>
            {paginationItems.map((item) => {
                if (typeof item !== 'number') {
                    return (
                        <span
                            key={item}
                            className="paginated-buttons__ellipsis"
                            aria-hidden="true"
                        >
                            ...
                        </span>
                    );
                }

                return (
                    <button
                        key={item}
                        type="button"
                        className={'text-button ' + (currentPage === item ? ' active' : '')}
                        onClick={() => onPageClick(item)}
                        aria-current={currentPage === item ? 'page' : undefined}
                        aria-label={`Page ${item}`}
                    >
                        {item}
                    </button>
                );
            })}
            <button
                type="button"
                className={'text-button paginated-buttons__arrow'}
                onClick={onForwardButtonClick}
                disabled={currentPage >= totalPages}
                aria-label="Next page"
            >
                <i className="fas fa-arrow-right"></i>
            </button>
        </nav>
    )
}

export default PaginationButtons
